import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PER_PAGE_OPTIONS, pageCount } from "@cms/config";
import { Button } from "./button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { cn } from "../lib/utils.js";

interface PaginationControlsProps {
  page: number;
  /** Total matching items (not just the current page). */
  total: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  perPageOptions?: number[];
  /** Noun for the count line, e.g. "posts" → "24 posts". */
  itemsLabel?: string;
  className?: string;
}

/**
 * Standard footer for paginated lists: count, per-page select, prev/next.
 * Renders nothing while everything fits on one smallest-size page, so short
 * lists stay uncluttered.
 */
export function PaginationControls({
  page,
  total,
  perPage,
  onPageChange,
  onPerPageChange,
  perPageOptions = PER_PAGE_OPTIONS,
  itemsLabel = "items",
  className,
}: PaginationControlsProps) {
  const totalPages = pageCount(total, perPage);

  // Clamp a page stranded past the end (e.g. the last item on the last page
  // was deleted, so the refetch came back empty): snap to the real last page
  // instead of showing an empty window the user can't obviously escape.
  React.useEffect(() => {
    if (page > totalPages) onPageChange(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onPageChange is a state setter
  }, [page, totalPages]);

  const smallest = Math.min(...perPageOptions);
  if (total <= smallest && page <= 1) return null;

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground", className)}>
      <span>
        {total} {itemsLabel}
      </span>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <span className="text-xs">Per page</span>
          <Select value={String(perPage)} onValueChange={(value: string) => onPerPageChange(Number(value))}>
            <SelectTrigger className="h-8 w-[4.5rem]" aria-label="Items per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {perPageOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="whitespace-nowrap">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
